import { useRef, useState } from 'react';
import {
  Store,
  Camera,
  FileBadge,
  Image as ImageIcon,
  CreditCard,
  BookOpen,
  HelpCircle,
  FileText,
  Shield,
  AlertTriangle,
  LogOut,
  Trash2,
  Loader2,
  Settings,
  DollarSign,
  Calendar,
  Info,
} from 'lucide-react';
import { doc, deleteDoc } from 'firebase/firestore';
import ChefHeader from '../shared/ui/ChefHeader';
import { ListRow, PrimaryButton } from '../shared/ui';
import { useAuth } from '../contexts/AuthContext';
import { auth, db } from '../shared/firebase';
import { uploadUserPhoto } from '../shared/services/userService';
import RestaurantFormSheet from '../components/profile/RestaurantFormSheet';
import BannerSheet from '../components/profile/BannerSheet';
import ConfirmDialog from '../components/menu/ConfirmDialog';
import BusinessPreferencesSheet from '../components/profile/BusinessPreferencesSheet';
import PermitSheet from '../components/profile/PermitSheet';

// Grouped iOS-style section: small uppercase label + white rounded card.
function Section({ title, children }) {
  return (
    <div>
      {title && (
        <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          {title}
        </p>
      )}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
        {children}
      </div>
    </div>
  );
}

// Mirrors iOS RestaurantProfileView ("Business Profile" tab).
export default function ProfilePage() {
  const { user, userProfile, restaurant, signOut } = useAuth();

  const [photoURL, setPhotoURL] = useState(
    userProfile?.profileImageURL || userProfile?.photoURL || ''
  );
  const [photoBusy, setPhotoBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showPermit, setShowPermit] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const photoInputRef = useRef(null);

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !user) return;
    setPhotoBusy(true);
    setError(null);
    try {
      const url = await uploadUserPhoto(user.uid, f);
      setPhotoURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const onSignOut = async () => {
    setBusy(true);
    try {
      await signOut();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  // Mirrors iOS deleteAccount: remove Firestore user data, then delete the
  // Firebase Auth account. requires-recent-login -> ask user to re-login.
  const onDeleteAccount = async () => {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      try {
        await deleteDoc(doc(db, 'users', user.uid));
      } catch (e) {
        console.warn('user doc delete failed:', e);
      }
      await auth.currentUser.delete();
      // Auth state change routes to /login automatically.
    } catch (e) {
      setBusy(false);
      if (e.code === 'auth/requires-recent-login') {
        setError(
          'For security reasons, please sign out and sign back in before deleting your account.'
        );
      } else {
        setError(
          'Failed to delete account. Please try again or contact support.'
        );
      }
    }
  };



  const openLink = (url) => () => window.open(url, '_blank', 'noopener');

  const isActive = restaurant ? restaurant.raw?.isActive !== false : false;
  const hours =
    restaurant?.raw?.openingHours ||
    (restaurant?.openingTime && restaurant?.closingTime
      ? `${restaurant.openingTime} - ${restaurant.closingTime}`
      : '');

  return (
    <div>
      <ChefHeader title="Business Profile" />

      {error && (
        <div className="mx-4 mt-3 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-xs font-medium">
          {error}
        </div>
      )}

      {!restaurant ? (
        // iOS "No Store Found" setup state
        <div className="flex flex-col items-center text-center px-6 pt-14 gap-4">
          <Store size={60} className="text-[#FF6B35]" />
          <p className="text-xl font-bold text-gray-900">No Store Found</p>
          <p className="text-sm text-gray-500">
            It looks like you don't have a store set up yet.
          </p>
          <PrimaryButton
            className="max-w-[260px]"
            onClick={() => setShowEdit(true)}
          >
            Set Up Your Store
          </PrimaryButton>
        </div>
      ) : (
        <div className="px-4 pt-4 pb-8 space-y-5">
          {/* Owner Profile */}
          <Section title="Owner Profile">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50"
            >
              <div className="relative shrink-0">
                {photoURL ? (
                  <img
                    src={photoURL}
                    alt="Owner"
                    className="w-[60px] h-[60px] rounded-full object-cover bg-gray-100"
                  />
                ) : (
                  <div className="w-[60px] h-[60px] rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                    <Camera size={26} />
                  </div>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-[#FF6B35] text-white flex items-center justify-center border-2 border-white">
                  {photoBusy ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Camera size={11} />
                  )}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  Owner Profile Photo
                </p>
                <p className="text-xs text-gray-500">
                  {photoURL ? 'Tap to change' : 'Add your photo'}
                </p>
              </div>
              <span className="text-gray-300">›</span>
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickPhoto}
            />
          </Section>

          {/* Store Information */}
          <Section title="Store Information">
            <ListRow label="Name" value={restaurant.name || '—'} chevron={false} />
            <ListRow label="Email" value={restaurant.email || '—'} chevron={false} />
            <ListRow label="Phone" value={restaurant.phone || '—'} chevron={false} />
            <ListRow
              label="Address"
              value={restaurant.address || '—'}
              chevron={false}
            />
            <ListRow
              label="Parish"
              value={restaurant.parish || '—'}
              chevron={false}
            />
            <ListRow
              label="Island"
              value={restaurant.island || '—'}
              chevron={false}
            />
            <div className="flex items-center gap-3 px-4 py-3.5">
              <span className="flex-1 text-sm font-medium text-gray-800">
                Status
              </span>
              <span
                className={`text-sm font-semibold ${
                  isActive ? 'text-green-600' : 'text-red-500'
                }`}
              >
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </Section>

          {/* Hours */}
          <Section title="Hours">
            <div className="px-4 py-3.5 text-sm text-gray-800">
              {hours || 'Not set'}
            </div>
          </Section>

          {/* Store Images */}
          <Section title="Store Images">
            <ListRow
              icon={ImageIcon}
              label="Banner Image"
              value={restaurant.bannerURL ? 'Uploaded' : 'Not Set'}
              onClick={() => setShowBanner(true)}
            />
          </Section>

          <Section>
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="w-full px-4 py-3.5 text-sm font-semibold text-[#FF6B35] text-center active:bg-gray-50"
            >
              Edit Store Information
            </button>
          </Section>

          {/* Payout */}
          <Section title="Payout">
            <ListRow
              icon={CreditCard}
              label="Payout Details"
              value={
                restaurant?.raw?.bankName && restaurant?.raw?.bankAccountNumber
                  ? `${restaurant.raw.bankName} ··${String(restaurant.raw.bankAccountNumber).slice(-4)}`
                  : 'Add bank details'
              }
              onClick={() => setShowEdit(true)}
            />
          </Section>

          {/* Documents */}
          <Section title="Documents">
            <ListRow
              icon={FileBadge}
              label="Food Handler's Permit"
              value={
                restaurant?.raw?.foodHandlerPermitStatus === 'approved' ? 'Approved'
                : restaurant?.raw?.foodHandlerPermitURL ? 'Pending Review'
                : 'Required'
              }
              onClick={() => setShowPermit(true)}
            />
          </Section>

          {/* Business Preferences */}
          <Section title="Business Settings">
            <ListRow
              icon={Settings}
              label="Business Preferences"
              onClick={() => setShowPreferences(true)}
            />
          </Section>

          {/* Help & Information */}
          <Section title="Help & Information">
            <ListRow
              icon={BookOpen}
              label="User Guide & FAQ"
              onClick={openLink('https://chefskn.com/guide')}
            />
            <ListRow
              icon={HelpCircle}
              label="Help & Support"
              onClick={openLink('mailto:support@chefskn.com')}
            />
          </Section>

          {/* Legal */}
          <Section title="Legal">
            <ListRow
              icon={FileText}
              label="Terms of Service"
              onClick={openLink('https://chefskn.com/terms')}
            />
            <ListRow
              icon={Shield}
              label="Privacy Policy"
              onClick={openLink('https://chefskn.com/privacy')}
            />
            <ListRow
              icon={AlertTriangle}
              label="Liability Waiver"
              onClick={openLink('https://chefskn.com/liability')}
            />
          </Section>

          {/* Payment & Commission */}
          <Section title="Payment & Commission">
            <ListRow
              icon={DollarSign}
              label="Commission Structure"
              value="10% per order"
              chevron={false}
            />
            <ListRow
              icon={Calendar}
              label="Payment Schedule"
              value="Daily payouts"
              chevron={false}
            />
            <ListRow
              icon={CreditCard}
              label="Payment Methods"
              value="Bank transfer"
              chevron={false}
            />
          </Section>

          {/* Payment Information note */}
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
            <div className="flex gap-3">
              <Info size={18} className="text-[#FF6B35] shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold text-gray-900 mb-1">Payment Information</p>
                <p className="text-xs">
                  Your first payout takes 2 days; after that, payouts are processed daily with no skipped days. You must have a valid bank account linked to receive payouts.
                  Contact support if you have questions about your payment schedule or commission structure.
                </p>
              </div>
            </div>
          </div>

          {/* About (iOS LabeledContent values) */}
          <Section title="About">
            <ListRow label="App Version" value="1.0.0" chevron={false} />
            <ListRow label="Operated by" value="Browne Ink" chevron={false} />
            <ListRow
              label="Support"
              value="+1 (869) 660-1052"
              onClick={openLink('tel:+18696601052')}
            />
          </Section>

          {/* Sign Out — always the last row */}
          <Section title="Account">
            <ListRow
              icon={LogOut}
              label="Sign Out"
              onClick={() => setConfirmSignOut(true)}
            />
          </Section>
        </div>
      )}

      <RestaurantFormSheet
        open={showEdit}
        onClose={() => setShowEdit(false)}
        restaurant={restaurant}
      />
      <BannerSheet open={showBanner} onClose={() => setShowBanner(false)} />
      <BusinessPreferencesSheet
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
      />
      <PermitSheet
        open={showPermit}
        onClose={() => setShowPermit(false)}
        restaurantId={restaurant?.id}
        restaurant={restaurant}
      />

      <ConfirmDialog
        open={confirmSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmLabel="Sign Out"
        busy={busy}
        onConfirm={onSignOut}
        onCancel={() => setConfirmSignOut(false)}
      />
    </div>
  );
}
